import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  createApiKey,
  getApiKeys,
  revokeApiKey,
  deleteApiKey,
  getUsageAnalytics,
  getApiLogs,
  getLogDetails,
} from "../../../services/developerService";

import ApiKeyManagement from "./ApiKeyManagement";
import UsageAnalytics from "./UsageAnalytics";
import RequestHistory from "./RequestHistory";
import CodeExamples from "./CodeExamples";
import CreateKeyModal from "./CreateKeyModal";
import LogDetailsModal from "./LogDetailsModal";

const DeveloperTools = () => {
  const { token } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("api-keys");
  const [activeCodeTab, setActiveCodeTab] = useState("python");
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [logTotalCount, setLogTotalCount] = useState(0);
  const [logLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [endpointFilter, setEndpointFilter] = useState("all");
  const [error, setError] = useState(null);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: "", description: "" });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logModalLoading, setLogModalLoading] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [copyStatus, setCopyStatus] = useState({});

  useEffect(() => {
    if (token) {
      loadDataForTab(activeTab);
    }
  }, [activeTab, token]);

  const loadDataForTab = async (tab) => {
    setLoading(true);
    setError(null);
    try {
      switch (tab) {
        case "api-keys":
          await loadApiKeys();
          break;
        case "analytics":
          await loadAnalytics();
          break;
        case "history":
          await loadLogs(logPage);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadApiKeys = async () => {
    try {
      const response = await getApiKeys(token);
      setApiKeys(response.apiKeys || []);
      const initialVisibility = {};
      const initialCopyStatus = {};
      (response.apiKeys || []).forEach((key) => {
        initialVisibility[key._id] = false;
        initialCopyStatus[key._id] = false;
      });
      setVisibleKeys(initialVisibility);
      setCopyStatus(initialCopyStatus);
    } catch (err) {
      console.error("Failed to load API keys:", err);
      setApiKeys([]);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await getUsageAnalytics(token);
      setAnalytics(response);
      debugger
    } catch (err) {
      console.error("Failed to load analytics:", err);
      setAnalytics({
        totalRequests: 13500,
        totalTokens: 496000,
        successRate: 98.7,
        avgResponseTime: 124,
        uptime: 98.7,
        errorRate: 1.3,
        bleuScore: 0.92,
        dailyUsage: {},
        topLanguagePairs: [
          { pair: "Yoruba To English", count: 45200, percentage: 15 },
          { pair: "Swahili To Yoruba", count: 32000, percentage: 12 },
          { pair: "Nigerian Pidgin To English", count: 28900, percentage: 8 },
          { pair: "English To Yoruba", count: 15800, percentage: 5 },
          { pair: "Other Combinations", count: 8500, percentage: 0 },
        ],
      });
    }
  };

  const loadLogs = async (page = 1) => {
    setLoading(true);
    try {
      const targetPage = typeof page === "number" ? page : 1;
      const params = {
        page: targetPage,
        limit: logLimit,
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(endpointFilter !== "all" && { endpoint: endpointFilter }),
        ...(searchTerm && { search: searchTerm }),
      };
      const response = await getApiLogs(params, token);
      const fetchedLogs = response.logs || (Array.isArray(response) ? response : []);
      setLogs(fetchedLogs);
      const total = response.total ?? response.totalLogs ?? response.count ?? fetchedLogs.length;
      setLogTotalCount(total);
      setLogTotalPages(response.totalPages || Math.max(1, Math.ceil(total / logLimit)));
      setLogPage(targetPage);
    } catch (err) {
      console.error("Failed to load logs:", err);
      setLogs([]);
      setLogTotalCount(0);
      setLogTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopyStatus((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => {
      setCopyStatus((prev) => ({ ...prev, [id]: false }));
    }, 2000);
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys((prev) => ({
      ...prev,
      [keyId]: !prev[keyId],
    }));
  };

  const handleCreateApiKey = async () => {
    if (!newKeyData.name.trim()) {
      setError("API key name is required");
      return;
    }
    setLoading(true);
    try {
      const response = await createApiKey(newKeyData, token);
      setVisibleKeys((prev) => ({ ...prev, [response._id]: false }));
      setCopyStatus((prev) => ({ ...prev, [response._id]: false }));
      setShowCreateKeyModal(false);
      setNewKeyData({ name: "", description: "" });
      setError(null);
      loadApiKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    setLoading(true);
    try {
      await revokeApiKey(keyId, token);
      setApiKeys((prev) =>
        prev.map((key) =>
          key._id === keyId ? { ...key, isActive: false } : key
        )
      );
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApiKey = async (keyId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this API key? This action cannot be undone."
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await deleteApiKey(keyId, token);
      setApiKeys((prev) => prev.filter((key) => key._id !== keyId));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogDetails = async (logId) => {
    if (!logId) return;

    // Immediately show whatever partial log info we have from the table list
    const localLog = logs.find((log) => log.id === logId || log._id === logId);
    if (localLog) {
      setSelectedLog(localLog);
    } else {
      setSelectedLog({ id: logId, _id: logId });
    }
    setShowLogModal(true);
    setLogModalLoading(true);

    // Call GET /api/developer/logs/${log.id} to fetch full object with successDetails
    try {
      const response = await getLogDetails(logId, token);
      const fullLog = response?.log || response?.data || response;
      if (fullLog) {
        setSelectedLog((prev) => ({ ...(prev || {}), ...fullLog }));
      }
    } catch (err) {
      console.error("Failed to fetch full log details:", err);
    } finally {
      setLogModalLoading(false);
    }
  };

  const handleSearchAndFilter = () => {
    loadLogs(1);
  };

  const handleLogPageChange = (newPage) => {
    loadLogs(newPage);
  };



  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Top Tab Navigation */}
      <div className="bg-[#F4F8F7] rounded-2xl p-1 shadow-sm">
        <nav className="flex flex-col md:flex-row space-y-1 md:space-y-0 md:space-x-1 justify-between my-2 mx-2">
          <button
            onClick={() => setActiveTab("api-keys")}
            className={`px-6 py-3 font-semibold text-sm rounded-xl transition-all duration-200 ${activeTab === "api-keys"
              ? "bg-secondary text-white shadow-sm"
              : "bg-transparent text-[#1A1A1A] hover:bg-gray-50"
              }`}
          >
            API Key Management
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`px-6 py-3 font-semibold text-sm rounded-xl transition-all duration-200 ${activeTab === "analytics"
              ? "bg-secondary text-white shadow-sm"
              : "bg-transparent text-[#1A1A1A] hover:bg-gray-50"
              }`}
          >
            Usage & Analytics
          </button>
          <button
            onClick={() => setActiveTab("guides")}
            className={`px-6 py-3 font-semibold text-sm rounded-xl transition-all duration-200 ${activeTab === "guides"
              ? "bg-secondary text-white shadow-sm"
              : "bg-transparent text-[#1A1A1A] hover:bg-gray-50"
              }`}
          >
            Code Examples
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-6 py-3 font-semibold text-sm rounded-xl transition-all duration-200 ${activeTab === "history"
              ? "bg-secondary text-white shadow-sm"
              : "bg-transparent text-[#1A1A1A] hover:bg-gray-50"
              }`}
          >
            Request History
          </button>
        </nav>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Tab Panels */}
      {activeTab === "api-keys" && (
        <ApiKeyManagement
          apiKeys={apiKeys}
          loading={loading}
          visibleKeys={visibleKeys}
          copyStatus={copyStatus}
          toggleKeyVisibility={toggleKeyVisibility}
          copyToClipboard={copyToClipboard}
          handleRevokeApiKey={handleRevokeApiKey}
          handleDeleteApiKey={handleDeleteApiKey}
          onOpenCreateModal={() => setShowCreateKeyModal(true)}
        />
      )}

      {activeTab === "analytics" && (
        <UsageAnalytics analytics={analytics} loading={loading} />
      )}

      {activeTab === "guides" && (
        <CodeExamples
          activeCodeTab={activeCodeTab}
          setActiveCodeTab={setActiveCodeTab}
          copyToClipboard={copyToClipboard}
          copyStatus={copyStatus}
        />
      )}

      {activeTab === "history" && (
        <RequestHistory
          logs={logs}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          endpointFilter={endpointFilter}
          setEndpointFilter={setEndpointFilter}
          handleSearchAndFilter={handleSearchAndFilter}
          handleViewLogDetails={handleViewLogDetails}
          currentPage={logPage}
          totalPages={logTotalPages}
          totalCount={logTotalCount}
          itemsPerPage={logLimit}
          onPageChange={handleLogPageChange}
          loading={loading}
        />
      )}

      {/* Modals */}
      <CreateKeyModal
        show={showCreateKeyModal}
        onClose={() => setShowCreateKeyModal(false)}
        onSubmit={handleCreateApiKey}
        loading={loading}
        newKeyData={newKeyData}
        setNewKeyData={setNewKeyData}
      />

      <LogDetailsModal
        show={showLogModal}
        selectedLog={selectedLog}
        loading={logModalLoading}
        onClose={() => {
          setShowLogModal(false);
          setSelectedLog(null);
        }}
      />
    </div>
  );
};

export default DeveloperTools;